import React, { useState, useEffect } from 'react';
import { API, graphqlOperation, Auth } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { searchCreditTransactions } from '../../../graphql/credits/queries';
import { useCredits } from '../../../hooks/useCredits';
import { SearchIcon, ChartIcon, RobotIcon, DownloadIcon, CreditCardIcon } from '../Icons';
import SkeletonLoader from '../ui/SkeletonLoader';
import './CreditUsageDashboard.scss';

const CreditUsageDashboard = () => {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [totalCreditsUsed, setTotalCreditsUsed] = useState(0);
  const [avgDailyUsage, setAvgDailyUsage] = useState(0);
  const [usageLoading, setUsageLoading] = useState(false);
  const [featureUsage, setFeatureUsage] = useState([]);
  const [featureUsageLoading, setFeatureUsageLoading] = useState(false);
  const { balance, subscriptionTier, subscriptionStatus } = useCredits();

  useEffect(() => {
    const fetchTransactionData = async () => {
      setUsageLoading(true);
      setFeatureUsageLoading(true);

      try {
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
        const userId = user.attributes.sub;

        // Calculate date for filtering
        const today = new Date();
        const startDate = new Date();
        startDate.setDate(today.getDate() - selectedPeriod);

        const { data } = await API.graphql(
          graphqlOperation(searchCreditTransactions, {
            filter: {
              userId: { matchPhrasePrefix: userId },
              createdAt: { gte: startDate.toISOString() },
              type: { eq: 'USAGE' },
            },
            aggregates: [{ name: 'operation', type: 'terms', field: 'operation' }],
          }),
        );

        // Process aggregation results for feature usage
        const buckets = data?.searchCreditTransactions?.aggregateItems?.[0]?.result?.buckets || [];
        setFeatureUsage(buckets);

        // Calculate total credits used from items
        const transactionItems = data?.searchCreditTransactions?.items || [];
        const totalAmount = transactionItems.reduce((sum, item) => sum + Math.abs(item.amount || 0), 0);

        setTotalCreditsUsed(totalAmount);

        // Calculate daily average
        const dailyAvg = totalAmount / Math.max(selectedPeriod, 1);
        setAvgDailyUsage(dailyAvg);
      } catch (error) {
        console.error('Failed to fetch transaction data:', error);
        setFeatureUsage([]);
        setTotalCreditsUsed(0);
        setAvgDailyUsage(0);
      } finally {
        setUsageLoading(false);
        setFeatureUsageLoading(false);
      }
    };

    fetchTransactionData();
  }, [selectedPeriod]);

  // Map operation types to display names
  const getOperationDisplayName = operation => {
    switch (operation) {
      case 'LOOK_FINDER':
      case 'VISUAL_SEARCH':
        return 'Visual Search';
      case 'PRICE_MONITORING_TIER_1':
      case 'PRICE_MONITORING_TIER_2':
      case 'PRICE_MONITORING_TIER_3':
      case 'PRICE_MONITORING_TIER_4':
        return 'Price Monitoring';
      case 'AI_ENHANCEMENT':
      case 'VIRTUAL_TRYON':
        return 'Virtual Try-On';
      default:
        return null; // Skip other operations
    }
  };

  // Process feature usage data
  const allFeatureTypes = ['Visual Search', 'Price Monitoring', 'Virtual Try-On'];

  const processedFeatureUsage = allFeatureTypes.map(featureType => {
    // Find all operations that map to this feature type
    const matchingBuckets = featureUsage.filter(bucket => getOperationDisplayName(bucket.key) === featureType);

    // Sum up all counts for this feature type
    const totalCount = matchingBuckets.reduce((sum, bucket) => sum + bucket.doc_count, 0);

    return {
      displayName: featureType,
      count: totalCount,
    };
  });

  const getOperationIcon = operation => {
    switch (operation) {
      case 'Visual Search':
      case 'VISUAL_SEARCH':
        return <SearchIcon />;
      case 'Price Monitoring':
      case 'PRICE_MONITORING':
        return <ChartIcon />;
      case 'Virtual Try-On':
      case 'VIRTUAL_TRYON':
        return <RobotIcon />;
      case 'EXPORT':
        return <DownloadIcon />;
      default:
        return <CreditCardIcon />;
    }
  };
  if (usageLoading) {
    return (
      <div className='credit-usage-dashboard'>
        <div className='dashboard-header'>
          <SkeletonLoader type='basic' height='24px' width='250px' style={{ marginBottom: '0' }} />
          <div className='period-selector'>
            <SkeletonLoader type='basic' height='36px' width='220px' borderRadius='6px' />
          </div>
        </div>

        <div className='dashboard-stats'>
          {[1, 2, 3, 4].map(index => (
            <div key={index} className='stat-card'>
              <SkeletonLoader type='basic' height='18px' width='120px' style={{ marginBottom: '16px' }} />
              <SkeletonLoader
                type='basic'
                height='36px'
                width='80px'
                style={{ marginBottom: '8px', margin: '0 auto' }}
              />
              <SkeletonLoader type='basic' height='14px' width='100px' style={{ margin: '0 auto' }} />
            </div>
          ))}
        </div>

        <div className='dashboard-content'>
          <div className='usage-breakdown'>
            <SkeletonLoader type='basic' height='24px' width='180px' style={{ marginBottom: '24px' }} />
            <div className='operation-list'>
              {[1, 2, 3].map(index => (
                <div key={index} className='operation-item'>
                  <div className='operation-info'>
                    <SkeletonLoader
                      type='basic'
                      height='24px'
                      width='24px'
                      borderRadius='6px'
                      style={{ marginRight: '12px' }}
                    />
                    <SkeletonLoader type='basic' height='18px' width='150px' />
                  </div>
                  <SkeletonLoader type='basic' height='18px' width='40px' />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className='credit-usage-dashboard'>
      <div className='dashboard-header'>
        <h2>{t('Credit Usage Dashboard')}</h2>
        <div className='period-selector'>
          {[7, 30, 90].map(days => (
            <button
              key={days}
              className={selectedPeriod === days ? 'active' : ''}
              onClick={() => setSelectedPeriod(days)}
            >
              {days} days
            </button>
          ))}
        </div>
      </div>

      <div className='dashboard-stats'>
        <div className='stat-card'>
          <h3>Current Balance</h3>
          <div className='stat-value'>{balance}</div>
          <div className='stat-label'>credits</div>
        </div>

        <div className='stat-card'>
          <h3>Credits Used</h3>
          <div className='stat-value'>{totalCreditsUsed}</div>
          <div className='stat-label'>last {selectedPeriod} days</div>
        </div>

        <div className='stat-card'>
          <h3>Daily Average</h3>
          <div className='stat-value'>{avgDailyUsage.toFixed(1)}</div>
          <div className='stat-label'>credits/day</div>
        </div>

        <div className='stat-card'>
          <h3>Subscription</h3>
          <div className='stat-value'>{subscriptionTier}</div>
          <div className='stat-label'>{subscriptionStatus}</div>
        </div>
      </div>

      <div className='dashboard-content'>
        <div className='usage-breakdown'>
          <h3>Usage by Feature</h3>
          {featureUsageLoading ? (
            <div className='operation-list'>
              {[1, 2, 3].map(index => (
                <div key={index} className='operation-item'>
                  <div className='operation-info'>
                    <SkeletonLoader
                      type='basic'
                      height='20px'
                      width='20px'
                      borderRadius='50%'
                      style={{ marginRight: '12px' }}
                    />
                    <SkeletonLoader type='basic' height='16px' width='120px' />
                  </div>
                  <SkeletonLoader type='basic' height='16px' width='30px' />
                </div>
              ))}
            </div>
          ) : (
            <div className='operation-list'>
              {processedFeatureUsage.map((item, index) => (
                <div key={index} className='operation-item'>
                  <div className='operation-info'>
                    <span className='operation-icon'>{getOperationIcon(item.displayName)}</span>
                    <span className='operation-name'>{item.displayName}</span>
                  </div>
                  <div className='operation-count'>{item.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default CreditUsageDashboard;
